import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Menu,
  Snackbar,
  Alert as MuiAlert,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useTranslation } from "react-i18next";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import ClearIcon from "@mui/icons-material/Clear";
import EditIcon from "@mui/icons-material/Edit";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import { FormControlLabel, Switch } from "@mui/material";
import ConfirmActionDialog from "./ConfirmActionDialog";
import {
  format,
  startOfMonth,
  endOfMonth,
  subMonths,
  addMonths,
} from "date-fns";
import leaveApiService from "../../../services/leave-api";
import {
  LeaveRequest,
  LeaveRequestStatus,
  LeaveType,
  formatDate,
} from "../../../types/leave";

// Statistics interface for filtered requests
interface FilteredStatistics {
  totalRequests: number;
  approvedRequests: number;
  pendingRequests: number;
  deniedRequests: number;
  plannedRequests: number;
  unplannedRequests: number;
}

const LeaveList: React.FC = () => {
  const { t } = useTranslation();
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter states - Default: 1st day of previous month to last day of next month
  const [startDate, setStartDate] = useState<Date | null>(
    startOfMonth(subMonths(new Date(), 1)),
  );
  const [endDate, setEndDate] = useState<Date | null>(
    endOfMonth(addMonths(new Date(), 1)),
  );
  const [employeeName, setEmployeeName] = useState("");
  const [employeeNameInput, setEmployeeNameInput] = useState(""); // Local state for input
  const [leaveTypeId, setLeaveTypeId] = useState<number | "">("");
  const [status, setStatus] = useState<LeaveRequestStatus | "">("");
  // Admin-only: when enabled, include soft-deleted (DELETED) requests
  const [showDeleted, setShowDeleted] = useState(false);

  // Confirmation dialog for cancel (approved) / delete (pending)
  const [confirmAction, setConfirmAction] = useState<{
    type: "cancel" | "delete";
    request: LeaveRequest;
  } | null>(null);

  // Menu state for planned status update
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRequestId, setSelectedRequestId] = useState<number | null>(
    null,
  );

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  // Calculate statistics from the filtered requests
  const statistics: FilteredStatistics = React.useMemo(() => {
    return {
      totalRequests: requests.length,
      approvedRequests: requests.filter((r) => r.status === "APPROVED").length,
      pendingRequests: requests.filter((r) => r.status === "PENDING").length,
      deniedRequests: requests.filter((r) => r.status === "DENIED").length,
      plannedRequests: requests.filter((r) => r.isPlanned === true).length,
      unplannedRequests: requests.filter((r) => r.isPlanned === false).length,
    };
  }, [requests]);

  const fetchLeaveTypes = async () => {
    try {
      const types = await leaveApiService.getLeaveTypes();
      setLeaveTypes(types);
    } catch (err) {
      console.error("Failed to fetch leave types:", err);
    }
  };

  const fetchLeaveRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await leaveApiService.getAllLeaveRequestsWithFilters({
        startDate: startDate ? format(startDate, "yyyy-MM-dd") : undefined,
        endDate: endDate ? format(endDate, "yyyy-MM-dd") : undefined,
        employeeName: employeeName || undefined,
        leaveTypeId: leaveTypeId || undefined,
        status: status || undefined,
        deleted: showDeleted || undefined,
      });
      setRequests(data);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("error.failedToFetchLeaveRequests"),
      );
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, employeeName, leaveTypeId, status, showDeleted, t]);

  useEffect(() => {
    fetchLeaveTypes();
  }, []);

  // Auto-fetch when filters change
  useEffect(() => {
    fetchLeaveRequests();
  }, [fetchLeaveRequests]);

  const handleClearFilters = () => {
    // Reset to default date range (1st of previous month to last day of next month)
    setStartDate(startOfMonth(subMonths(new Date(), 1)));
    setEndDate(endOfMonth(addMonths(new Date(), 1)));
    setEmployeeName("");
    setEmployeeNameInput("");
    setLeaveTypeId("");
    setStatus("");
    setShowDeleted(false);
  };

  const handleEmployeeNameKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Enter") {
      setEmployeeName(employeeNameInput);
    }
  };

  const handleEmployeeNameBlur = () => {
    setEmployeeName(employeeNameInput);
  };

  const getStatusColor = (status: LeaveRequestStatus) => {
    switch (status) {
      case LeaveRequestStatus.APPROVED:
        return "success";
      case LeaveRequestStatus.PENDING:
        return "warning";
      case LeaveRequestStatus.DENIED:
        return "error";
      case LeaveRequestStatus.CANCELLED:
        return "default";
      case LeaveRequestStatus.DELETED:
        return "default";
      default:
        return "default";
    }
  };

  // Runs after the admin confirms a cancel (approved) or delete (pending) action.
  const handleConfirmAction = async () => {
    if (!confirmAction) return;
    const { type, request } = confirmAction;
    if (type === "cancel") {
      await leaveApiService.cancelLeaveRequestAsAdmin(request.id);
    } else {
      await leaveApiService.deleteLeaveRequest(request.id);
    }
    setSnackbar({
      open: true,
      message:
        type === "cancel"
          ? t("leave.messages.requestCancelled", "Leave request cancelled")
          : t("leave.messages.requestDeleted", "Leave request deleted"),
      severity: "success",
    });
    fetchLeaveRequests();
  };

  const getPlannedLabel = (isPlanned: boolean | null | undefined) => {
    if (isPlanned === null || isPlanned === undefined) return "-";
    return isPlanned ? t("leave.planned") : t("leave.unplanned");
  };

  const getPlannedColor = (isPlanned: boolean | null | undefined) => {
    if (isPlanned === null || isPlanned === undefined) return "default";
    return isPlanned ? "info" : "warning";
  };

  const handlePlannedMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    requestId: number,
  ) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedRequestId(requestId);
  };

  const handlePlannedMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedRequestId(null);
  };

  const handleUpdatePlannedStatus = async (isPlanned: boolean) => {
    if (!selectedRequestId) return;
    try {
      await leaveApiService.updatePlannedStatus(selectedRequestId, isPlanned);
      setSnackbar({
        open: true,
        message: t("leave.plannedStatusUpdated"),
        severity: "success",
      });
      // Refresh the list to show updated status
      fetchLeaveRequests();
    } catch (err) {
      setSnackbar({
        open: true,
        message:
          err instanceof Error
            ? err.message
            : t("error.failedToUpdatePlannedStatus"),
        severity: "error",
      });
    } finally {
      handlePlannedMenuClose();
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleExportCsv = () => {
    const headers = [
      "Employee",
      "Leave Type",
      "Start Date",
      "End Date",
      "Days",
      "Status",
      "Planned/Unplanned",
      "Reason",
      "Approver",
      "Created At",
    ];

    const csvData = requests.map((req) => [
      req.employeeName,
      req.leaveTypeName,
      formatDate(req.startDate),
      formatDate(req.endDate),
      req.totalDays,
      req.status,
      req.isPlanned === null || req.isPlanned === undefined
        ? ""
        : req.isPlanned
          ? "Planned"
          : "Unplanned",
      req.reason || "",
      req.approverName || "",
      formatDate(req.createdAt),
    ]);

    const csvContent = [
      headers.join(","),
      ...csvData.map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `leave_list_${format(new Date(), "yyyy-MM-dd")}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ pt: 0, pr: 2, pb: 2, pl: 2 }}>
        {/* Filters Section */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            {t("common.filters")}
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <DatePicker
                label={t("leave.startDate")}
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <DatePicker
                label={t("leave.endDate")}
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField
                label={t("leave.employee")}
                value={employeeNameInput}
                onChange={(e) => setEmployeeNameInput(e.target.value)}
                onBlur={handleEmployeeNameBlur}
                onKeyDown={handleEmployeeNameKeyDown}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("leave.leaveType")}</InputLabel>
                <Select
                  value={leaveTypeId}
                  label={t("leave.leaveType")}
                  onChange={(e) =>
                    setLeaveTypeId(e.target.value as number | "")
                  }
                >
                  <MenuItem value="">{t("common.all")}</MenuItem>
                  {leaveTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {t(`leave.leaveTypes.${type.code}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("common.status")}</InputLabel>
                <Select
                  value={status}
                  label={t("common.status")}
                  onChange={(e) =>
                    setStatus(e.target.value as LeaveRequestStatus | "")
                  }
                >
                  <MenuItem value="">{t("common.all")}</MenuItem>
                  <MenuItem value={LeaveRequestStatus.PENDING}>
                    {t("leave.status.PENDING")}
                  </MenuItem>
                  <MenuItem value={LeaveRequestStatus.APPROVED}>
                    {t("leave.status.APPROVED")}
                  </MenuItem>
                  <MenuItem value={LeaveRequestStatus.DENIED}>
                    {t("leave.status.DENIED")}
                  </MenuItem>
                  <MenuItem value={LeaveRequestStatus.CANCELLED}>
                    {t("leave.status.CANCELLED")}
                  </MenuItem>
                  {showDeleted && (
                    <MenuItem value={LeaveRequestStatus.DELETED}>
                      {t("leave.status.DELETED")}
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Tooltip title={t("common.clearFilters")}>
                <IconButton onClick={handleClearFilters} size="small">
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Paper>

        {/* Statistics Summary */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t("leave.statistics")}
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, sm: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <AssignmentIcon fontSize="small" color="primary" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t("leave.reports.totalRequests")}
                  </Typography>
                  <Typography variant="h6" color="primary.main">
                    {statistics.totalRequests}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CheckCircleIcon fontSize="small" color="success" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t("leave.reports.approved")}
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {statistics.approvedRequests}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <HourglassEmptyIcon fontSize="small" color="warning" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t("leave.pendingRequests")}
                  </Typography>
                  <Typography variant="h6" color="warning.main">
                    {statistics.pendingRequests}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CancelIcon fontSize="small" color="error" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t("leave.reports.denied")}
                  </Typography>
                  <Typography variant="h6" color="error.main">
                    {statistics.deniedRequests}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t("leave.planned")}
                </Typography>
                <Typography variant="h6" color="info.main">
                  {statistics.plannedRequests}
                  {statistics.plannedRequests + statistics.unplannedRequests >
                    0 && (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ ml: 0.5 }}
                    >
                      (
                      {Math.round(
                        (statistics.plannedRequests /
                          (statistics.plannedRequests +
                            statistics.unplannedRequests)) *
                          100,
                      )}
                      %)
                    </Typography>
                  )}
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t("leave.unplanned")}
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  {statistics.unplannedRequests}
                  {statistics.plannedRequests + statistics.unplannedRequests >
                    0 && (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ ml: 0.5 }}
                    >
                      (
                      {Math.round(
                        (statistics.unplannedRequests /
                          (statistics.plannedRequests +
                            statistics.unplannedRequests)) *
                          100,
                      )}
                      %)
                    </Typography>
                  )}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Actions Bar */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t("common.totalRecords")}: {requests.length}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showDeleted}
                  onChange={(e) => {
                    const next = e.target.checked;
                    setShowDeleted(next);
                    // If turning off while the DELETED status filter is active, clear it
                    if (!next && status === LeaveRequestStatus.DELETED) {
                      setStatus("");
                    }
                  }}
                  size="small"
                />
              }
              label={t("leave.showDeleted")}
            />
            <Tooltip title={t("common.refresh")}>
              <IconButton onClick={fetchLeaveRequests} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportCsv}
              disabled={requests.length === 0}
              size="small"
            >
              {t("leave.exportCsv")}
            </Button>
          </Box>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : requests.length === 0 ? (
          <Alert severity="info">{t("leave.noRequests")}</Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("leave.employee")}</TableCell>
                  <TableCell>{t("leave.leaveType")}</TableCell>
                  <TableCell>{t("leave.dates")}</TableCell>
                  <TableCell
                    align="center"
                    sx={{ textTransform: "capitalize" }}
                  >
                    {t("leave.days")}
                  </TableCell>
                  <TableCell align="center">{t("common.status")}</TableCell>
                  <TableCell align="center">
                    {t("leave.plannedStatus")}
                  </TableCell>
                  <TableCell>{t("leave.reason")}</TableCell>
                  <TableCell>{t("leave.approver")}</TableCell>
                  <TableCell align="center">{t("common.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((request) => (
                  <TableRow key={request.id} hover>
                    <TableCell>{request.employeeName}</TableCell>
                    <TableCell>
                      {t(`leave.leaveTypes.${request.leaveTypeCode}`)}
                    </TableCell>
                    <TableCell>
                      {formatDate(request.startDate)} -{" "}
                      {formatDate(request.endDate)}
                    </TableCell>
                    <TableCell align="center">{request.totalDays}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={t(`leave.status.${request.status}`)}
                        color={getStatusColor(request.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={t("leave.clickToChangePlannedStatus")}>
                        <Chip
                          label={getPlannedLabel(request.isPlanned)}
                          color={getPlannedColor(request.isPlanned)}
                          size="small"
                          variant="outlined"
                          onClick={(e) => handlePlannedMenuOpen(e, request.id)}
                          icon={<EditIcon fontSize="small" />}
                          sx={{ cursor: "pointer" }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 200,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {request.reason || "-"}
                    </TableCell>
                    <TableCell>{request.approverName || "-"}</TableCell>
                    <TableCell align="center">
                      {request.status === LeaveRequestStatus.APPROVED ? (
                        /* Admin may cancel an APPROVED leave regardless of date */
                        <Tooltip title={t("leave.cancelRequest")}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              setConfirmAction({ type: "cancel", request })
                            }
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : request.status === LeaveRequestStatus.PENDING ? (
                        /* Admin may delete a PENDING leave regardless of date */
                        <Tooltip title={t("leave.deleteRequest")}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              setConfirmAction({ type: "delete", request })
                            }
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Menu for changing planned status */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handlePlannedMenuClose}
        >
          <MenuItem onClick={() => handleUpdatePlannedStatus(true)}>
            <Chip
              label={t("leave.planned")}
              color="info"
              size="small"
              variant="outlined"
              sx={{ mr: 1 }}
            />
            {t("leave.planned")}
          </MenuItem>
          <MenuItem onClick={() => handleUpdatePlannedStatus(false)}>
            <Chip
              label={t("leave.unplanned")}
              color="warning"
              size="small"
              variant="outlined"
              sx={{ mr: 1 }}
            />
            {t("leave.unplanned")}
          </MenuItem>
        </Menu>

        {/* Confirmation dialog for cancel (approved) / delete (pending) */}
        <ConfirmActionDialog
          open={confirmAction !== null}
          title={
            confirmAction?.type === "cancel"
              ? t("leave.confirmCancelTitle")
              : t("leave.confirmDeleteTitle")
          }
          message={
            confirmAction?.type === "cancel"
              ? t("leave.confirmCancelMessage")
              : t("leave.confirmDeleteMessage")
          }
          details={
            confirmAction ? (
              <>
                <Typography variant="body2" fontWeight={600}>
                  {confirmAction.request.employeeName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t(`leave.leaveTypes.${confirmAction.request.leaveTypeCode}`)}
                  {": "}
                  {formatDate(confirmAction.request.startDate)} -{" "}
                  {formatDate(confirmAction.request.endDate)} (
                  {confirmAction.request.totalDays} {t("leave.days")})
                </Typography>
              </>
            ) : undefined
          }
          confirmLabel={
            confirmAction?.type === "cancel"
              ? t("leave.cancelRequest")
              : t("leave.deleteRequest")
          }
          cancelLabel={t("common.close")}
          confirmColor="error"
          onConfirm={handleConfirmAction}
          onClose={() => setConfirmAction(null)}
        />

        {/* Snackbar for feedback */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <MuiAlert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            elevation={6}
            variant="filled"
          >
            {snackbar.message}
          </MuiAlert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default LeaveList;
